import React from 'react';
import { translate } from 'react-jhipster';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

export interface IRebateDocumentAttachSetting {
  enabled: boolean;
  documents: string[];
}

export const emptyDocumentAttachSetting: IRebateDocumentAttachSetting = { enabled: false, documents: [] };

// Order matches the "5. OPTIONAL DOCUMENTS TO SEND" mockup, left column then right column.
export const REBATE_DOCUMENT_TYPES: string[] = ['quotationForm', 'transferForm', 'purchaseForm', 'sellForm'];

export const isDocumentAttachSettingValid = (value: IRebateDocumentAttachSetting) => !value.enabled || value.documents.length > 0;

/**
 * Reads the document attach setting saved for a bank/commodityType from the JSON-encoded `rebateDocumentAttachSettings*` column.
 */
export const documentAttachSettingFromBank = (settingsJson?: string | null): IRebateDocumentAttachSetting => {
  if (settingsJson) {
    try {
      const parsed = JSON.parse(settingsJson);
      if (parsed && typeof parsed.enabled === 'boolean' && Array.isArray(parsed.documents)) {
        return { enabled: parsed.enabled, documents: parsed.documents.filter(d => REBATE_DOCUMENT_TYPES.includes(d)) };
      }
    } catch {
      // not valid JSON; fall through to the default below
    }
  }
  return emptyDocumentAttachSetting;
};

interface DocumentSectionProps {
  value: IRebateDocumentAttachSetting;
  onChange: (value: IRebateDocumentAttachSetting) => void;
  showErrors: boolean;
}

export const DocumentSection = ({ value, onChange, showErrors }: DocumentSectionProps) => {
  const error = showErrors && !isDocumentAttachSettingValid(value);

  const toggleDocument = (document: string) => {
    const documents = value.documents.includes(document) ? value.documents.filter(d => d !== document) : [...value.documents, document];
    onChange({ ...value, documents });
  };

  return (
    <Box sx={{ maxWidth: { md: '75%' } }}>
      <FormControlLabel
        control={<Switch checked={value.enabled} onChange={e => onChange({ ...value, enabled: e.target.checked })} />}
        label={translate('comexApp.bank.rebate.document.enable')}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(value.enabled ? 'comexApp.bank.rebate.document.enabledHint' : 'comexApp.bank.rebate.document.disabledHint')}
      </Typography>

      {value.enabled && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {translate('comexApp.bank.rebate.document.documentsLabel')}
          </Typography>
          <FormGroup>
            <Grid container spacing={0}>
              {REBATE_DOCUMENT_TYPES.map(document => (
                <Grid item xs={12} sm={6} key={document}>
                  <FormControlLabel
                    control={<Checkbox checked={value.documents.includes(document)} onChange={() => toggleDocument(document)} />}
                    label={translate(`comexApp.bank.rebate.document.documents.${document}`)}
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>
          {error && (
            <Typography variant="caption" color="error" sx={{ mx: 1.75 }}>
              {translate('comexApp.bank.rebate.document.required')}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};
